// External sources (Plex + Jellyfin) — thin client wrappers around
// /api/sources/* endpoints. Mirrors the style of `liveTv.js` and `admin.js`:
// all requests go through `apiFetch` so cookie auth, JSON parsing, and the
// 401 → /locked redirect are handled uniformly with the rest of the app.
//
// All endpoints are scoped on the server by the device cookie + invite token
// id; the client never needs to pass an account / server identifier for
// authentication. The `serverId` / `accountId` arguments below are entity
// ids assigned by the server (sources_plex / sources_jellyfin tables).
//
// IMAGE + STREAM helpers return absolute path strings — the player and
// <img> elements load them directly without going through fetch().

import { apiFetch } from "./apiClient";

// ---------------------------------------------------------------------------
// Plex
// ---------------------------------------------------------------------------

// Keep the dependency status/body for retry and auth handling while giving
// grids, shelves and players a useful message when they display Error.message.
async function plexFetch(url, options) {
  try {
    return await apiFetch(url, options);
  } catch (error) {
    if (error?.body?.error === "plex_access_denied") {
      error.message = "Plex denied access to this server. The account owner needs to check sharing access or reconnect Plex.";
    }
    throw error;
  }
}

export function listAccounts() {
  return plexFetch("/api/sources/plex/accounts");
}

/**
 * Start the Plex PIN-OAuth flow. Returns `{ pinId, authUrl, expiresAt }`.
 * The caller is responsible for opening `authUrl` in a popup (see Task 30)
 * and polling `pollPlexConnect(pinId)` until `status` becomes 'connected'.
 */
export function connectPlexStart() {
  return plexFetch("/api/sources/plex/connect", { method: "POST" });
}

/**
 * Poll once for the result of a Plex PIN-OAuth flow.
 * Returns `{ status: 'pending' | 'connected' | 'expired', ... }`.
 */
export function pollPlexConnect(pinId) {
  return plexFetch(`/api/sources/plex/connect/${encodeURIComponent(pinId)}`);
}

export function disconnectPlexAccount(accountId) {
  return plexFetch(
    `/api/sources/plex/accounts/${encodeURIComponent(accountId)}`,
    {
      method: "DELETE",
    },
  );
}

export function refreshPlexAccount(accountId) {
  return plexFetch(
    `/api/sources/plex/accounts/${encodeURIComponent(accountId)}/refresh`,
    {
      method: "POST",
    },
  );
}

export function listPlexLibraries(serverId) {
  return plexFetch(
    `/api/sources/plex/${encodeURIComponent(serverId)}/libraries`,
  );
}

/**
 * Paginated browse of a Plex library section.
 *
 * @param {number|string} serverId
 * @param {number|string} sectionId
 * @param {{ start?: number, size?: number, sort?: string, audioLanguage?: string }} [opts]
 *   `audioLanguage` is the language code as returned by the Plex
 *   `/languages` endpoint — in practice the 2-letter ISO-639-1 code (ja/fr/ko).
 *   The server lowercases and validates `^[a-z]{2,3}$`, so 2- or 3-letter both
 *   pass; pass through whatever the languages list gave you (Feature 38/41).
 */
export function browsePlexSection(serverId, sectionId, opts = {}) {
  const { start, size, sort, audioLanguage, genre } = opts;
  const qs = new URLSearchParams();
  if (start != null) qs.set("start", String(start));
  if (size != null) qs.set("size", String(size));
  if (sort) qs.set("sort", sort);
  if (audioLanguage) qs.set("audioLanguage", String(audioLanguage));
  if (genre != null && genre !== "") qs.set("genre", String(genre));
  const suffix = qs.toString() ? `?${qs}` : "";
  return plexFetch(
    `/api/sources/plex/${encodeURIComponent(serverId)}/libraries/${encodeURIComponent(sectionId)}/items${suffix}`,
  );
}

/**
 * Library-aware "More like this" hubs for a Plex item (Feature 37b).
 * NON-personalized: the server has already dropped On Deck / Continue
 * Watching hubs and stripped per-viewer watch state. Returns
 * `{ hubs: [{ title, hubIdentifier, type, items: [...] }] }`.
 *
 * @param {number|string} serverId
 * @param {number|string} ratingKey
 */
export function getPlexRelated(serverId, ratingKey) {
  return plexFetch(
    `/api/sources/plex/${encodeURIComponent(serverId)}/items/${encodeURIComponent(ratingKey)}/related`,
  );
}

/**
 * List the genres present in a Plex library section (Feature 37b). A
 * library taxonomy read, not a viewer signal. Returns
 * `{ genres: [{ id, title }] }`.
 *
 * @param {number|string} serverId
 * @param {number|string} sectionId
 */
export function getPlexSectionGenres(serverId, sectionId) {
  return plexFetch(
    `/api/sources/plex/${encodeURIComponent(serverId)}/libraries/${encodeURIComponent(sectionId)}/genres`,
  );
}

/**
 * List the audio languages present in a Plex library section (Feature 38).
 * A library taxonomy read, not a viewer signal. Returns
 * `{ languages: [{ id, title }] }` where `id` is the language code Plex
 * reports (in practice the 2-letter ISO-639-1 code, e.g. ja/fr/ko) and
 * `title` the human label.
 *
 * @param {number|string} serverId
 * @param {number|string} sectionId
 */
export function getPlexSectionLanguages(serverId, sectionId) {
  return plexFetch(
    `/api/sources/plex/${encodeURIComponent(serverId)}/libraries/${encodeURIComponent(sectionId)}/languages`,
  );
}

/**
 * Fetch a Plex library section's items filtered to a single genre
 * (Feature 37b). Reuses the section-items route with a `?genre=` filter
 * (a library filter, not a viewer signal — stays non-personalized).
 * Items come back safe-projected. Returns `{ items: [...] }`.
 *
 * @param {number|string} serverId
 * @param {number|string} sectionId
 * @param {number|string} genreId
 * @param {{ size?: number, sort?: string, audioLanguage?: string }} [opts]
 *   `audioLanguage` is the code from the Plex `/languages` endpoint (2-letter
 *   ISO-639-1 in practice, e.g. ja/fr/ko) — Feature 38/41. Combinable with the
 *   genre filter.
 */
export function getPlexGenreItems(serverId, sectionId, genreId, opts = {}) {
  const { size, sort, audioLanguage } = opts;
  const qs = new URLSearchParams();
  qs.set("genre", String(genreId));
  if (size != null) qs.set("size", String(size));
  if (sort) qs.set("sort", sort);
  if (audioLanguage) qs.set("audioLanguage", String(audioLanguage));
  return plexFetch(
    `/api/sources/plex/${encodeURIComponent(serverId)}/libraries/${encodeURIComponent(sectionId)}/items?${qs}`,
  );
}

/**
 * Plex item detail. `mode` may be undefined (item detail), 'children'
 * (seasons of a show), or 'allLeaves' (flat episodes).
 */
export function getPlexItem(serverId, ratingKey, mode) {
  const sId = encodeURIComponent(serverId);
  const rk = encodeURIComponent(ratingKey);
  if (mode === "children") {
    return plexFetch(`/api/sources/plex/${sId}/items/${rk}/children`);
  }
  if (mode === "allLeaves") {
    return plexFetch(`/api/sources/plex/${sId}/items/${rk}/allLeaves`);
  }
  return plexFetch(`/api/sources/plex/${sId}/items/${rk}`);
}

/**
 * Build the Plex stream URL for a `<video>` element. Returns an absolute
 * path string — the player loads it directly (the server 307s to the real
 * upstream URL, with Referrer-Policy: no-referrer so the token doesn't
 * leak via Referer).
 *
 * @param {number|string} serverId
 * @param {number|string} ratingKey
 * @param {{ mode?: 'direct'|'hls', partId?: number|string, maxBitrate?: number, subtitleStreamId?: number|string }} [opts]
 *   `maxBitrate` is **Kbps** (server cap: 100000 = 100 Mbps). Sending raw
 *   bps gets rejected with HTTP 400 invalid_max_bitrate.
 *   `subtitleStreamId` is the Plex stream id of the subtitle to burn into
 *   the HLS transcode. Omit or pass 0 to disable subtitles. HLS-only;
 *   direct-play ignores it (the user picks via the browser's native
 *   subtitle UI on direct play).
 */
export function streamPlexUrl(serverId, ratingKey, opts = {}) {
  const {
    mode = "hls",
    partId,
    maxBitrate,
    subtitleStreamId,
    mediaIndex,
  } = opts;
  const sId = encodeURIComponent(serverId);
  const rk = encodeURIComponent(ratingKey);
  // HLS mode goes through the CORS pass-through proxy so hls.js stays
  // same-origin (Plex servers almost never set Access-Control-Allow-Origin
  // and the page is https while the Plex LAN URI is often http/self-signed,
  // both fatal for hls.js + native cross-origin manifest fetch).
  if (mode === "hls") {
    const qs = new URLSearchParams();
    qs.set("mode", "hls");
    if (maxBitrate != null) qs.set("maxBitrate", String(maxBitrate));
    // Which Media version to transcode (only sent when non-default, so
    // existing single-version URLs are byte-for-byte unchanged).
    if (Number.isInteger(mediaIndex) && mediaIndex > 0) {
      qs.set("mediaIndex", String(mediaIndex));
    }
    if (
      subtitleStreamId != null &&
      String(subtitleStreamId) !== "" &&
      String(subtitleStreamId) !== "0"
    ) {
      qs.set("subtitleStreamID", String(subtitleStreamId));
    }
    return `/api/sources/plex/${sId}/items/${rk}/proxy/master.m3u8?${qs}`;
  }
  // Direct mode streams the ORIGINAL file THROUGH the proxy (Range-aware).
  // It used to 307-redirect the browser straight at the Plex server, but
  // most Plex servers are LAN-only / set no CORS, so a native <video>
  // pointed at them just hangs for remote users. Proxying the raw bytes
  // plays browser-compatible files natively with no transcode load.
  if (mode === "direct" && partId != null) {
    return `/api/sources/plex/${sId}/items/${rk}/proxy/file?partId=${encodeURIComponent(
      partId,
    )}`;
  }
  // No part id to direct-stream — fall back to the legacy 307 stream route.
  const qs = new URLSearchParams();
  qs.set("mode", mode);
  return `/api/sources/plex/${sId}/items/${rk}/stream?${qs}`;
}

/**
 * Mint a public Chromecast-ready stream URL for a Plex item. The in-app HLS
 * proxy is cookie-gated (a Chromecast can't send the device cookie), so this
 * authed call returns an absolute, token-authed master.m3u8 the receiver can
 * fetch on its own. Pass the same maxBitrate/subtitle the player is using so
 * the cast transcode matches.
 *
 * @returns {Promise<{ url: string }>}
 */
export function getPlexCastUrl(serverId, ratingKey, opts = {}) {
  const { maxBitrate, subtitleStreamId } = opts;
  const sId = encodeURIComponent(serverId);
  const rk = encodeURIComponent(ratingKey);
  const qs = new URLSearchParams();
  if (maxBitrate != null) qs.set("maxBitrate", String(maxBitrate));
  if (
    subtitleStreamId != null &&
    String(subtitleStreamId) !== "" &&
    String(subtitleStreamId) !== "0"
  ) {
    qs.set("subtitleStreamID", String(subtitleStreamId));
  }
  const query = qs.toString();
  return plexFetch(
    `/api/sources/plex/${sId}/items/${rk}/cast-url${query ? `?${query}` : ""}`,
  );
}

/**
 * Build the Plex image proxy URL. `opts.url` is the relative Plex image
 * path (must start with `/`). Returns an absolute path string suitable for
 * `<img src>`.
 *
 * @param {number|string} serverId
 * @param {{ url: string, w: number, h: number }} opts
 */
export function plexImageUrl(serverId, opts = {}) {
  const { url, w, h } = opts;
  const qs = new URLSearchParams();
  if (url != null) qs.set("url", String(url));
  if (w != null) qs.set("w", String(w));
  if (h != null) qs.set("h", String(h));
  return `/api/sources/plex/${encodeURIComponent(serverId)}/image?${qs}`;
}

// ---------------------------------------------------------------------------
// Jellyfin
// ---------------------------------------------------------------------------

export function listServers() {
  return apiFetch("/api/sources/jellyfin/servers");
}

/**
 * Connect a new Jellyfin (or Emby) server. The server requires either
 * `username + password` OR `apiKey`. `name` and `url` are mandatory.
 *
 * @param {{
 *   name: string,
 *   url: string,
 *   username?: string,
 *   password?: string,
 *   apiKey?: string,
 * }} payload
 */
export function connectJellyfin(payload) {
  return apiFetch("/api/sources/jellyfin/connect", {
    method: "POST",
    json: payload,
  });
}

export function disconnectJellyfinServer(serverId) {
  return apiFetch(`/api/sources/jellyfin/${encodeURIComponent(serverId)}`, {
    method: "DELETE",
  });
}

export function refreshJellyfinServer(serverId) {
  return apiFetch(
    `/api/sources/jellyfin/${encodeURIComponent(serverId)}/refresh`,
    {
      method: "POST",
    },
  );
}

export function listJellyfinViews(serverId) {
  return apiFetch(
    `/api/sources/jellyfin/${encodeURIComponent(serverId)}/views`,
  );
}

/**
 * Paginated browse over a Jellyfin server.
 *
 * @param {number|string} serverId
 * @param {{
 *   parentId?: string,
 *   type?: 'Movie'|'Series'|'Episode',
 *   start?: number,
 *   size?: number,
 *   sort?: string,
 * }} [opts]
 */
export function listJellyfinItems(serverId, opts = {}) {
  const { parentId, type, start, size, sort } = opts;
  const qs = new URLSearchParams();
  if (parentId) qs.set("parentId", parentId);
  if (type) qs.set("type", type);
  if (start != null) qs.set("start", String(start));
  if (size != null) qs.set("size", String(size));
  if (sort) qs.set("sort", sort);
  const suffix = qs.toString() ? `?${qs}` : "";
  return apiFetch(
    `/api/sources/jellyfin/${encodeURIComponent(serverId)}/items${suffix}`,
  );
}

export function getJellyfinItem(serverId, itemId) {
  return apiFetch(
    `/api/sources/jellyfin/${encodeURIComponent(serverId)}/items/${encodeURIComponent(itemId)}`,
  );
}

/**
 * Build the Jellyfin stream URL for a `<video>` element. Returns an
 * absolute path string. The server 307s to the upstream URL with
 * Referrer-Policy: no-referrer.
 *
 * @param {number|string} serverId
 * @param {string} itemId
 * @param {{ mode?: 'direct'|'hls', maxBitrate?: number }} [opts]
 *   `maxBitrate` is **Kbps** (server cap: 100000). The server multiplies
 *   by 1000 internally for Jellyfin's PlaybackInfo `maxStreamingBitrate`.
 */
export function streamJellyfinUrl(serverId, itemId, opts = {}) {
  const { mode = "hls", maxBitrate } = opts;
  const qs = new URLSearchParams();
  qs.set("mode", mode);
  if (mode === "hls" && maxBitrate != null)
    qs.set("maxBitrate", String(maxBitrate));
  return `/api/sources/jellyfin/${encodeURIComponent(serverId)}/items/${encodeURIComponent(itemId)}/stream?${qs}`;
}

/**
 * Build the Jellyfin image proxy URL.
 *
 * @param {number|string} serverId
 * @param {{ itemId: string, type: 'Primary'|'Backdrop'|'Logo'|'Thumb', maxWidth: number }} opts
 */
export function jellyfinImageUrl(serverId, opts = {}) {
  const { itemId, type, maxWidth } = opts;
  const qs = new URLSearchParams();
  if (itemId != null) qs.set("itemId", String(itemId));
  if (type) qs.set("type", String(type));
  if (maxWidth != null) qs.set("maxWidth", String(maxWidth));
  return `/api/sources/jellyfin/${encodeURIComponent(serverId)}/image?${qs}`;
}

// ---------------------------------------------------------------------------
// Cross-source match
// ---------------------------------------------------------------------------

/**
 * Find every Plex / Jellyfin server attached to the invite that holds the
 * given TMDB item. Returns an array (possibly empty).
 *
 * @param {number|string} tmdbId
 * @param {'movie'|'tv'} type
 */
export function getMatch(tmdbId, type) {
  const qs = new URLSearchParams();
  qs.set("tmdb_id", String(tmdbId));
  qs.set("type", String(type));
  return apiFetch(`/api/sources/match?${qs}`);
}

/**
 * Same lookup, but as `{ matches, incomplete }`. `incomplete: true` means a
 * server never answered, so an empty `matches` is "we don't know" rather than
 * "not in the library" — /browse needs that distinction to avoid promoting
 * Download over a copy the user owns.
 *
 * @param {number|string} tmdbId
 * @param {'movie'|'tv'} type
 */
export function getMatchDetailed(tmdbId, type) {
  const qs = new URLSearchParams();
  qs.set("tmdb_id", String(tmdbId));
  qs.set("type", String(type));
  qs.set("detail", "1");
  return apiFetch(`/api/sources/match?${qs}`);
}

// ---------------------------------------------------------------------------
// Sharing (Feature 28)
// ---------------------------------------------------------------------------

/**
 * List shares granted BY me + shares granted TO me. Used by Settings to
 * render the "Shares you've granted" and "Shared with you" sections.
 * Returns `{ grantedBy: [...], grantedTo: [...] }`.
 */
export function listShares() {
  return apiFetch("/api/sources/shares");
}

/**
 * Create (or upsert) a share. `kind` ∈ {'plex_server','jellyfin_server'}.
 * 201 on first create; 200 on idempotent re-share. 403 when the requester
 * lacks `share-external-sources` or doesn't own the source row.
 */
export function createShare({ kind, sourceRowId, sharedWithInviteTokenId }) {
  return apiFetch("/api/sources/shares", {
    method: "POST",
    json: { kind, sourceRowId, sharedWithInviteTokenId },
  });
}

/**
 * Revoke a share. The requester must be either the sharer or the sharee;
 * anyone else gets 404 (no existence leak). Returns 204 on success.
 */
export function revokeShare(shareId) {
  return apiFetch(`/api/sources/shares/${encodeURIComponent(shareId)}`, {
    method: "DELETE",
  });
}

/**
 * List OTHER invite tokens that could receive a share, with a
 * `hasUseExternalSources` flag so the UI can warn before sharing to an
 * invite that can't actually play the shared sources.
 */
export function listCandidateInvitesForShare() {
  return apiFetch("/api/sources/shares/candidate-invites");
}

// Default-export bag for components that prefer a namespaced import shape.
export const sources = {
  listAccounts,
  connectPlexStart,
  pollPlexConnect,
  disconnectPlexAccount,
  refreshPlexAccount,
  listPlexLibraries,
  browsePlexSection,
  getPlexRelated,
  getPlexSectionGenres,
  getPlexSectionLanguages,
  getPlexGenreItems,
  getPlexItem,
  streamPlexUrl,
  plexImageUrl,
  listServers,
  connectJellyfin,
  disconnectJellyfinServer,
  refreshJellyfinServer,
  listJellyfinViews,
  listJellyfinItems,
  getJellyfinItem,
  streamJellyfinUrl,
  jellyfinImageUrl,
  getMatch,
  getMatchDetailed,
  listShares,
  createShare,
  revokeShare,
  listCandidateInvitesForShare,
};
